import React, { useState, useMemo } from 'react';
import { Box, Grid, Paper, Typography, Button, Pagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Article as ArticleIcon } from '@mui/icons-material';
import { BlogLayout, BlogCard, useBlogSearch } from '../../components/blog';
import { blogPosts, categories } from '../../data/blog.generated';
import { BLOG_CATEGORIES } from '../../types/blog';
import { useDocumentTitle } from '../../hooks/core';

const POSTS_PER_PAGE = 6;

export const BlogHomePage: React.FC = () => {
  useDocumentTitle('Blog');
  const navigate = useNavigate();
  const { query, setQuery, results } = useBlogSearch(blogPosts);
  const [page, setPage] = useState(1);

  const featuredPosts = blogPosts.slice(0, 3);
  const allDisplayPosts = query ? results : blogPosts;

  const totalPages = Math.ceil(allDisplayPosts.length / POSTS_PER_PAGE);
  const displayPosts = useMemo(() => {
    const startIndex = (page - 1) * POSTS_PER_PAGE;
    return allDisplayPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [allDisplayPosts, page]);

  // Reset to page 1 when search query changes
  React.useEffect(() => {
    setPage(1);
  }, [query]);

  const pageDescription = 'Tips, strategies, and updates for running the perfect sports squares pool.';

  return (
    <BlogLayout
      title="SquareUp Blog"
      description={pageDescription}
      showSearch
      onSearch={setQuery}
      searchValue={query}
    >
      <Helmet>
        <title>Blog | SquareUp</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content="SquareUp Blog" />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content="https://squareitup.app/blog" />
        <meta property="og:site_name" content="SquareUp" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="SquareUp Blog" />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href="https://squareitup.app/blog" />
      </Helmet>
      {/* Featured section - only show when not searching */}
      {!query && featuredPosts.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)',
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="overline" sx={{ opacity: 0.9 }}>
                Latest Post
              </Typography>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {featuredPosts[0]?.title || 'Welcome to the SquareUp Blog'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                {featuredPosts[0]?.description ||
                  'Stay tuned for tips, strategies, and updates about running sports squares pools.'}
              </Typography>
              {featuredPosts[0] && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(`/blog/${featuredPosts[0].slug}`)}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  Read Article
                </Button>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'center', display: { xs: 'none', md: 'block' } }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                }}
              >
                <ArticleIcon sx={{ fontSize: 60 }} />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Search results or all posts */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        {query ? `Search Results for "${query}"` : 'All Posts'}
      </Typography>

      {displayPosts.length > 0 ? (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {displayPosts.map((post) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.slug}>
                <BlogCard post={post} />
              </Grid>
            ))}
          </Grid>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => {
                  setPage(value);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {query ? 'No posts found' : 'No blog posts yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {query
              ? 'Try a different search term or browse by category.'
              : 'Check back soon for new content!'}
          </Typography>
        </Paper>
      )}

      {/* Browse by category - only show when not searching */}
      {!query && categories.length > 0 && (
        <>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3, mt: 4 }}>
            Browse by Category
          </Typography>
          <Grid container spacing={2}>
            {BLOG_CATEGORIES.filter((cat) => categories.includes(cat.value)).map((category) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={category.value}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: category.color,
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => navigate(`/blog/category/${category.value}`)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: `${category.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: category.color,
                      }}
                    >
                      <ArticleIcon />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {category.label}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </BlogLayout>
  );
};

export default BlogHomePage;
